import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type {
  DetachReport,
  EventWithContext,
  PhaseWithStats,
  TimeEntryWithContext,
} from '@epasu/core';
import { PROJECT_STATUSES, PROJECT_STATUS_LABELS } from '@epasu/core/constants';
import type { ProjectStatus } from '@epasu/core/constants';
import {
  Activity,
  ArrowLeft,
  Briefcase,
  CalendarDays,
  Mail,
  Paperclip,
  StickyNote,
  Trash2,
} from 'lucide-react';
import {
  crm,
  type CustomerRow,
  type FileRow,
  type OrderRow,
  type ProjectRow,
  type TaskRow,
  type TimelineRow,
} from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { RichTextArea } from '@/components/RichTextArea';
import { listLabel } from '@/lib/backLink';
import { formatDateTime } from '@/lib/utils';
import { PhasesCard } from './project/PhasesCard';
import { TimeCard } from './project/TimeCard';
import { ProjectProgress, ProjectRiskBand } from '@/components/ProjectRiskBand';
import { OrdersCard } from './project/OrdersCard';
import { FilesCard } from './project/FilesCard';
import { EventsCard } from './project/EventsCard';

/**
 * Obergrenze je Liste in der Seitenleiste.
 *
 * Früher holte diese Seite die global neuesten 200 Aufgaben, Dateien und
 * Termine und filterte im Browser auf `projectId` — wuchs eine der Listen
 * systemweit über 200 hinaus, fehlten Einträge dieses Projekts, ohne dass
 * die Seite es gemeldet hätte. Seit `tasks.list`, `orders.list`,
 * `files.list` und `calendar.events` den Filter selbst kennen, zählt die
 * Grenze dort, wo sie hingehört: gegen die Einträge *dieses* Projekts.
 */
const SEITEN_LIMIT = 200;

const SYMBOL: Record<TimelineRow['kind'], { icon: typeof StickyNote; badge: string }> = {
  note: { icon: StickyNote, badge: 'bg-amber-100 text-amber-700' },
  message: { icon: Mail, badge: 'bg-sky-100 text-sky-700' },
  activity: { icon: Activity, badge: 'bg-slate-100 text-slate-700' },
  file: { icon: Paperclip, badge: 'bg-violet-100 text-violet-700' },
  event: { icon: CalendarDays, badge: 'bg-emerald-100 text-emerald-700' },
};

type Impact = DetachReport & { notes: number };

/**
 * Die Projektakte.
 *
 * Notizen, Schriftverkehr, Statuswechsel, Dateien und Termine stehen im
 * Verlauf chronologisch nebeneinander — eine Liste statt fünf Reiter, weil
 * wer den Vorgang verstehen will, die Reihenfolge braucht. Die Seitenleiste
 * trägt, was nicht in den Verlauf gehört: die aktiven Aufgaben, Aufträge,
 * Dateien und Termine zum Anfassen.
 */
export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [projekt, setProjekt] = useState<ProjectRow | null>(null);
  const [kunde, setKunde] = useState<CustomerRow | null>(null);
  const [timeline, setTimeline] = useState<TimelineRow[]>([]);
  const [phasen, setPhasen] = useState<PhaseWithStats[]>([]);
  const [zeiten, setZeiten] = useState<TimeEntryWithContext[]>([]);
  const [aufgaben, setAufgaben] = useState<TaskRow[]>([]);
  const [auftraege, setAuftraege] = useState<OrderRow[]>([]);
  const [dateien, setDateien] = useState<FileRow[]>([]);
  const [termine, setTermine] = useState<EventWithContext[]>([]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [impact, setImpact] = useState<Impact | null>(null);

  const laden = useCallback(async () => {
    if (!projectId) return;
    const gefunden = await crm.projects.get(projectId);
    if (!gefunden) {
      setProjekt(null);
      setNotFound(true);
      setLoading(false);
      return;
    }
    const [
      verlauf,
      k,
      phasenListe,
      zeitenListe,
      aufgabenListe,
      auftraegeListe,
      dateienListe,
      termineListe,
    ] = await Promise.all([
        crm.projects.timeline(projectId),
        crm.customers.byOrganization(gefunden.organizationId),
        crm.projects.phases(projectId),
        crm.timeEntries.list({ projectId }),
        crm.tasks.list({ projectId, limit: SEITEN_LIMIT }),
        crm.orders.list({ projectId, limit: SEITEN_LIMIT }),
        crm.files.list({ projectId, limit: SEITEN_LIMIT }),
        // `from` ist hier kein Widerspruch zum Verzicht auf die Systemuhr an
        // anderer Stelle: die Karte meint „kommende Termine“, das *ist* die
        // Bedeutung von „jetzt“ an dieser Stelle, keine Berechnung auf
        // „heute“. Ohne `from` liefert die Standardsortierung (aufsteigend
        // nach `startAt`) die 200 ältesten Termine systemweit statt der
        // nächsten anstehenden — bei einem Jahr synchronisierter Historie
        // reicht ein Kalenderkonto, um das Limit zu reißen.
        //
        // Der Filter sitzt auf `startAt`, nicht auf `endAt` — ein Termin, der
        // vor einer Stunde begann und noch zwei läuft, ist aus Sicht der
        // Leserin immer noch „kommend“, würde aber ohne Puffer schon
        // rausfallen. Zwölf Stunden decken jeden realistischen Termin ab, der
        // gerade läuft.
        crm.calendar.events({
          projectId,
          from: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          limit: SEITEN_LIMIT,
        }),
      ]);
    setProjekt(gefunden);
    setKunde(k);
    setTimeline(verlauf);
    setPhasen(phasenListe);
    setZeiten(zeitenListe);
    setAufgaben(aufgabenListe);
    setAuftraege(auftraegeListe);
    setDateien(dateienListe);
    setTermine(termineListe);
    setNotFound(false);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Der Hinweis ist eine einmalige Rückmeldung auf den gerade ausgeführten
  // Statuswechsel, kein Dauerzustand — ein Reload durch eine andere Aktion
  // (Notiz, Aufgabe, …) lässt ihn unberührt, erst der nächste Wechsel ersetzt
  // ihn.
  const [statusFehler, setStatusFehler] = useState<string | null>(null);
  const statusAendern = useCallback(
    async (neu: ProjectStatus) => {
      if (!projekt) return;
      if (neu === 'abgeschlossen' && projekt.openTasks > 0) {
        setHinweis(
          `${projekt.openTasks} ${projekt.openTasks === 1 ? 'Aufgabe ist' : 'Aufgaben sind'} ` +
            'noch offen. Das Projekt gilt trotzdem als abgeschlossen.',
        );
      } else {
        setHinweis(null);
      }
      setStatusFehler(null);
      try {
        await crm.projects.setStatus(projekt.id, neu);
        await laden();
      } catch (err) {
        setStatusFehler(err instanceof Error ? err.message : 'Status ändern fehlgeschlagen');
      }
    },
    [projekt, laden],
  );

  // „Stand“ wird nur beim erstmaligen Laden dieses Projekts aus dem Server
  // übernommen. Ein Reload durch eine andere Aktion (Notiz, Aufgabe, Status)
  // während der Nutzer gerade tippt, soll das Feld nicht überschreiben — der
  // Vergleich hängt deshalb an der Projekt-ID, nicht am ganzen Objekt.
  const [stand, setStand] = useState('');
  const standProjektId = useRef<string | null>(null);
  useEffect(() => {
    if (projekt && standProjektId.current !== projekt.id) {
      setStand(projekt.summary ?? '');
      standProjektId.current = projekt.id;
    }
  }, [projekt]);

  const [standFehler, setStandFehler] = useState<string | null>(null);
  const standSpeichern = useCallback(async () => {
    if (!projekt || stand === (projekt.summary ?? '')) return;
    setStandFehler(null);
    try {
      await crm.projects.update(projekt.id, { summary: stand.trim() || null });
      await laden();
    } catch (err) {
      // Nicht zurücksetzen: das Getippte bleibt stehen, aber sichtbar
      // ungespeichert — sonst verschwindet der Text kommentarlos.
      setStandFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }, [projekt, stand, laden]);

  const [notizText, setNotizText] = useState('');
  const [notizBusy, setNotizBusy] = useState(false);
  const [notizFehler, setNotizFehler] = useState<string | null>(null);

  const notizSpeichern = useCallback(async () => {
    if (!projekt || !notizText.trim()) return;
    setNotizBusy(true);
    setNotizFehler(null);
    try {
      await crm.projects.addNote({ projectId: projekt.id, body: notizText.trim() });
      setNotizText('');
      await laden();
    } catch (err) {
      setNotizFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setNotizBusy(false);
    }
  }, [projekt, notizText, laden]);

  // Eine gemeinsame Fehlermeldung für beide Verlauf-Aktionen: pro Zeile eine
  // eigene wäre hier Aufwand ohne Nutzen — es passiert nie beides zugleich,
  // und die Meldung steht sichtbar über der Liste statt stillschweigend zu
  // verpuffen, wenn `updateNote`/`deleteNote` fehlschlägt.
  const [verlaufFehler, setVerlaufFehler] = useState<string | null>(null);

  const notizAnpinnen = useCallback(
    async (noteId: string, pinned: boolean) => {
      setVerlaufFehler(null);
      try {
        await crm.projects.updateNote(noteId, { pinned: !pinned });
        await laden();
      } catch (err) {
        setVerlaufFehler(err instanceof Error ? err.message : 'Anpinnen fehlgeschlagen');
      }
    },
    [laden],
  );

  const notizLoeschen = useCallback(
    async (noteId: string) => {
      setVerlaufFehler(null);
      try {
        await crm.projects.deleteNote(noteId);
        await laden();
      } catch (err) {
        setVerlaufFehler(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
      }
    },
    [laden],
  );

  const loeschenStarten = useCallback(async () => {
    if (!projekt) return;
    const daten = await crm.projects.deletionImpact(projekt.id);
    setImpact(daten);
    setDeleting(true);
  }, [projekt]);

  // Loading-Rückgabe kommt vor dieser Stelle: solange `loading` steht, ist
  // `projekt` nicht verlässlich, und mit ihm fällig auch das Rückziel.
  if (loading) return <p className="text-sm text-muted-foreground">Lade …</p>;

  // Mehrere Eingänge — Projektliste und Projektreiter der Firma — geben ihre
  // Herkunft als Router-State mit; nur ein kalter Link (Reload, eingefügte
  // URL) hat keine und fällt dann auf die eigene Firma des Projekts zurück.
  const herkunft = (location.state as { from?: string } | null)?.from;
  // Ein internes Projekt hat keine Firma; `/firmen/` mit leerer Id führte ins
  // Leere. Für sie ist die Projektliste der richtige Rückweg — dort stehen
  // sie, und einen anderen Ort haben sie nicht.
  const eigeneFirma =
    projekt && projekt.organizationId ? `/firmen/${projekt.organizationId}` : '/projekte';
  const zurueckZu = herkunft ?? eigeneFirma;
  // Der Firmenname gilt nur, wenn der Weg auch wirklich zur Firma dieses
  // Projekts führt. Seit die globale Suche von überall hierher springt, kann
  // die Herkunft jeder Pfad sein — und ein Label, das einen Kunden nennt und
  // zum Kalender führt, ist schlimmer als ein schlichtes „Zurück“.
  const zurueckLabel =
    listLabel(zurueckZu) ??
    (zurueckZu === eigeneFirma && projekt?.customerName
      ? `Zurück zu ${projekt.customerName}`
      : 'Zurück');

  if (notFound || !projekt) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Dieses Projekt gibt es nicht (mehr).
        </p>
        <Button variant="outline" onClick={() => navigate(zurueckZu)}>
          Zurück zur Übersicht
        </Button>
      </Card>
    );
  }

  const angepinnt = timeline.filter((e) => e.kind === 'note' && Boolean(e.meta.pinned));
  // Angepinnte Notizen stehen oben in einem eigenen Block — unten dürfen sie
  // nicht noch einmal auftauchen, sonst zeigt der Verlauf denselben Eintrag
  // zweimal.
  const restVerlauf = timeline.filter((e) => !(e.kind === 'note' && Boolean(e.meta.pinned)));

  return (
    <div className="space-y-4">
      <Link
        to={zurueckZu}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {zurueckLabel}
      </Link>

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              <Briefcase className="h-5 w-5 text-muted-foreground" />
              {projekt.title}
            </h1>
            <p className="text-sm text-muted-foreground">{projekt.customerName}</p>

            <ProjectRiskBand risks={projekt.risks} />
            <ProjectProgress progress={projekt.progress} className="max-w-xs" />

            <Select
              aria-label="Status"
              value={projekt.status}
              onChange={(e) => void statusAendern(e.target.value as ProjectStatus)}
              className="h-8 w-auto text-xs"
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PROJECT_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
            {statusFehler && <p className="text-xs text-danger">{statusFehler}</p>}

            {hinweis && <p className="text-sm text-muted-foreground">{hinweis}</p>}

            <div className="max-w-xl space-y-1">
              <Input
                aria-label="Stand"
                value={stand}
                onChange={(e) => setStand(e.target.value)}
                onBlur={() => void standSpeichern()}
                placeholder="Wie ist der Stand?"
              />
              {standFehler && <p className="text-xs text-danger">{standFehler}</p>}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            aria-label="Projekt löschen"
            onClick={() => void loeschenStarten()}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Phasen stehen über dem Verlauf und in der breiten Spalte: der
              Verlauf wird gelesen, an den Phasen wird gearbeitet. In der
              Seitenleiste war die Karte so schmal, dass Phasentitel und das
              Auswahlfeld zum Verschieben nebeneinander nicht mehr passten. */}
          <PhasesCard
            projectId={projekt.id}
            phases={phasen}
            tasks={aufgaben}
            onChanged={() => void laden()}
          />
          <Card className="space-y-5 p-6">
            <div>
              <h2 className="text-sm font-semibold">Verlauf</h2>
              <div className="mt-2">
                <RichTextArea
                  value={notizText}
                  onChange={setNotizText}
                  rows={4}
                  placeholder="Was ist passiert?"
                />
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                {notizFehler && <p className="text-xs text-danger">{notizFehler}</p>}
                <Button
                  type="button"
                  size="sm"
                  className="ml-auto"
                  disabled={notizBusy || !notizText.trim()}
                  onClick={() => void notizSpeichern()}
                >
                  {notizBusy ? 'Speichere …' : 'Notiz speichern'}
                </Button>
              </div>
            </div>

            {verlaufFehler && <p className="text-xs text-danger">{verlaufFehler}</p>}

            {angepinnt.length > 0 && (
              <div className="border-t border-border pt-4">
                <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <StickyNote className="h-3.5 w-3.5" />
                  Angepinnt
                </h3>
                <ul className="mt-2 space-y-3">
                  {angepinnt.map((eintrag) => (
                    <VerlaufEintrag
                      key={eintrag.id}
                      eintrag={eintrag}
                      onAnpinnen={notizAnpinnen}
                      onLoeschen={notizLoeschen}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t border-border pt-4">
              {timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Noch nichts passiert. Die erste Notiz eröffnet den Verlauf.
                </p>
              ) : restVerlauf.length === 0 ? null : (
                <ul className="space-y-3">
                  {restVerlauf.map((eintrag) => (
                    <VerlaufEintrag
                      key={eintrag.id}
                      eintrag={eintrag}
                      onAnpinnen={notizAnpinnen}
                      onLoeschen={notizLoeschen}
                    />
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <TimeCard
            projectId={projekt.id}
            entries={zeiten}
            budgetCents={projekt.budgetCents}
            orderValueCents={projekt.orderValueCents}
            onChanged={() => void laden()}
          />
          <OrdersCard
            projectId={projekt.id}
            initialContactId={kunde?.primaryContactId ?? undefined}
            orders={auftraege}
            onChanged={() => void laden()}
          />
          <FilesCard projectId={projekt.id} files={dateien} onChanged={() => void laden()} />
          <EventsCard events={termine} />
        </div>
      </div>

      {deleting && impact && (
        <ConfirmDialog
          title={`Projekt „${projekt.title}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setDeleting(false)}
          onConfirm={async () => {
            await crm.projects.delete(projekt.id);
            navigate(zurueckZu);
          }}
        >
          <p className="text-sm text-muted-foreground">
            Aufträge, Nachrichten, Dateien, Aufgaben und Termine bleiben erhalten und
            hängen danach wieder direkt am Kunden. {impact.notes}{' '}
            {impact.notes === 1 ? 'Notiz wird' : 'Notizen werden'} gelöscht.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function VerlaufEintrag({
  eintrag,
  onAnpinnen,
  onLoeschen,
}: {
  eintrag: TimelineRow;
  onAnpinnen: (noteId: string, pinned: boolean) => void;
  onLoeschen: (noteId: string) => void;
}) {
  const { icon: Icon, badge } = SYMBOL[eintrag.kind];
  const istNotiz = eintrag.kind === 'note';
  const notizId = istNotiz ? (eintrag.meta.noteId as string) : null;
  const pinned = istNotiz && Boolean(eintrag.meta.pinned);

  return (
    <li className="flex gap-3">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${badge}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 pb-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className="text-sm font-medium">{eintrag.title}</p>
          <time className="shrink-0 text-xs text-muted-foreground">
            {formatDateTime(eintrag.at)}
          </time>
        </div>
        {eintrag.body && (
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted-foreground">
            {eintrag.body}
          </p>
        )}
        {notizId && (
          <div className="mt-1 flex gap-3 text-xs text-muted-foreground">
            <button
              type="button"
              className="hover:text-foreground hover:underline"
              onClick={() => onAnpinnen(notizId, pinned)}
            >
              {pinned ? 'Lösen' : 'Anpinnen'}
            </button>
            <button
              type="button"
              className="hover:text-destructive hover:underline"
              onClick={() => onLoeschen(notizId)}
            >
              Löschen
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
